/**
 * MODULE: _shared/frontend-shell/src/components/assistant/AssistantContextSelfLearningSections.tsx
 * PURPOSE: Explainability + learned preference blocks for the assistant context rail [2.2.1218].
 * DEPENDENCIES: brainInsightLinks, BrainInsightPreferenceHint
 * PUBLIC_API: AssistantContextExplainability, AssistantContextLearnedPreferences, AssistantContextPromotionPipeline, AssistantContextHarnessNote
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-05 [2.2.1490] — “Why promoted” subtitle on learned preference rows.
 * RECENT_PRIOR: 2026-05-26 [2.2.1233] — AssistantContextHarnessNote for v4 token transparency in context rail.
 * RECENT_PRIOR: 2026-05-25 [2.2.1224] — structured supporting_events in explainability rail.
 */

import { Badge } from 'flowbite-react';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  AssistantContextInsight,
  AssistantLearnedPreference,
  AssistantPromotionPipeline,
  AssistantRepContactPatterns,
} from '@shared/lib/frontend/api/assistantContextApi';
import type { BrainContextHarness, BrainUserLearningStatus } from '@shared/lib/frontend/api/brainMemoryApi';
import { BrainRepContactPatternsHint } from '../brain/BrainRepContactPatternsHint';
import { brainLearnedPreferencesSettingsPath } from '@shared/lib/frontend/utils/brainInsightLinks';
import { BrainInsightPreferenceHint } from '../brain/BrainInsightPreferenceHint';
import { formatLearnedPreferenceExplanation } from '../brain/brainLearnedPreferenceExplainUtils';
import { dispatchAssistantOpenWithPrefill } from '../../design-system/components/AiAssistant/assistantPrefillBridge';
import { shouldShowBrainLearningStatusBanner } from '@shared/lib/frontend/api/brainMemoryApi';
import { BrainLearningStatusBanner } from '../brain/BrainLearningStatusBanner';
import { AiAssistantMarkdown } from '../../design-system/components/AiAssistant/AiAssistantMarkdown';

function stripMarkdownHeading(text: string): string {
  return text.replace(/^##[^\n]*\n?/m, '').trim();
}

function confidenceLabel(confidence?: number): string | null {
  const c = typeof confidence === 'number' ? confidence : 0;
  if (c <= 0) return null;
  if (c >= 0.85) return 'High confidence';
  if (c >= 0.7) return 'Medium confidence';
  return 'Learning';
}

export function AssistantContextExplainability({
  text,
  insights = [],
}: {
  text?: string;
  insights?: AssistantContextInsight[];
}) {
  const body = stripMarkdownHeading(text || '');
  const insightRows = insights
    .filter((item) => (item.title || '').trim() || (item.supporting_events?.length ?? 0) > 0)
    .slice(0, 3);

  if (!body && insightRows.length === 0) return null;

  return (
    <section
      aria-label="Signal explainability"
      className="rounded-lg border border-violet-200 bg-violet-50/90 p-3 dark:border-violet-900/50 dark:bg-violet-950/30"
      data-testid="assistant-context-explainability"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200">
        Why this signal
      </p>
      {insightRows.length > 0 ? (
        <ul className="mt-2 space-y-2" data-testid="assistant-context-explainability-insights">
          {insightRows.map((insight) => (
            <li key={insight.id || `${insight.entity_id}-${insight.insight_type}`}>
              {insight.title ? (
                <p className="text-xs font-medium text-violet-950 dark:text-violet-100">{insight.title}</p>
              ) : null}
              {insight.supporting_events && insight.supporting_events.length > 0 ? (
                <ul
                  className="mt-1 space-y-1 text-[11px] text-violet-900 dark:text-violet-100"
                  data-testid="assistant-context-explainability-events"
                >
                  {insight.supporting_events.slice(0, 4).map((ev) => (
                    <li key={ev.id || `${ev.event_type}-${ev.title}`} className="flex flex-wrap items-center gap-1.5">
                      <span>{ev.title || ev.event_type || 'Activity'}</span>
                      {ev.source ? (
                        <Badge color="purple" size="xs">
                          {ev.source}
                        </Badge>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {insight.preference_metadata?.using_preference_for ||
              insight.preference_metadata?.preference_label ? (
                <BrainInsightPreferenceHint
                  text={
                    insight.preference_metadata.using_preference_for?.trim() ||
                    `Using your preference for ${insight.preference_metadata.preference_label}`
                  }
                  testId="assistant-context-explainability-preference"
                  showManageLink
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {body ? (
        <div className="mt-2 text-xs text-violet-950 dark:text-violet-100">
          <AiAssistantMarkdown content={body} compact />
        </div>
      ) : null}
      {insightRows.length > 0 || body ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button
            type="button"
            className="rounded-full border border-violet-300/80 bg-white/80 px-2 py-0.5 text-[10px] font-medium text-violet-900 transition hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100 dark:hover:bg-violet-900/50"
            data-testid="assistant-context-explainability-prompt"
            onClick={() =>
              dispatchAssistantOpenWithPrefill(
                insightRows[0]?.title
                  ? `Explain why "${insightRows[0].title}" matters for my pipeline.`
                  : 'Explain why this signal matters for my pipeline.',
              )
            }
          >
            Explain this signal
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function AssistantContextLearnedPreferences({
  items,
}: {
  items: AssistantLearnedPreference[];
}) {
  const visible = items.filter((item) => (item.label || '').trim()).slice(0, 3);
  if (visible.length === 0) return null;

  return (
    <section
      aria-label="Learned usage patterns"
      className="rounded-lg border border-violet-100 bg-violet-50/60 p-3 dark:border-violet-900/40 dark:bg-violet-950/20"
      data-testid="assistant-context-learned-preferences"
    >
      <div className="mb-2 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" aria-hidden />
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200">
          Learned from your usage
        </h3>
      </div>
      <ul className="space-y-2 text-xs text-violet-900 dark:text-violet-100">
        {visible.map((item) => {
          const hint = confidenceLabel(item.confidence);
          const label = (item.label || '').trim();
          const explanation = formatLearnedPreferenceExplanation({
            memory_key: item.memory_key,
            label: item.label,
            confidence: item.confidence,
            recurrence_count: item.recurrence_count,
          });
          return (
            <li
              key={item.memory_key || item.label}
              className="rounded-md border border-transparent p-1"
              data-testid="assistant-context-learned-pref"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span>{label}</span>
                {hint ? (
                  <Badge color="purple" size="xs" data-testid="assistant-context-learned-pref-confidence">
                    {hint}
                  </Badge>
                ) : null}
              </div>
              <p
                className="mt-1 text-[11px] leading-relaxed text-violet-800/90 dark:text-violet-200/90"
                data-testid="assistant-context-learned-pref-explanation"
              >
                {explanation}
              </p>
              {label ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    className="rounded-full border border-violet-300/80 bg-white/80 px-2 py-0.5 text-[10px] font-medium text-violet-900 transition hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100 dark:hover:bg-violet-900/50"
                    data-testid="assistant-context-learned-pref-prompt"
                    onClick={() =>
                      dispatchAssistantOpenWithPrefill(
                        `How does "${label}" influence your answers about my deals?`,
                      )
                    }
                  >
                    How does this shape answers?
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <Link
        to={brainLearnedPreferencesSettingsPath()}
        className="mt-2 inline-block text-[11px] font-medium text-violet-700 underline decoration-violet-400/60 underline-offset-2 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-100"
        data-testid="assistant-context-learned-manage-link"
      >
        Manage learned preferences
      </Link>
    </section>
  );
}

export function AssistantContextLearningStatus({
  status,
}: {
  status?: BrainUserLearningStatus | null;
}) {
  if (!shouldShowBrainLearningStatusBanner(status)) return null;
  return (
    <BrainLearningStatusBanner
      status={status}
      testId="assistant-context-learning-status"
      className="mb-0"
      showSettingsLink
    />
  );
}

export function AssistantContextPromotionPipeline({
  pipeline,
}: {
  pipeline?: AssistantPromotionPipeline | null;
}) {
  const pending = pipeline?.pending_candidates ?? 0;
  const eligible = pipeline?.eligible_candidates ?? 0;
  const almostReady = pipeline?.almost_ready_candidates ?? 0;
  const active = pipeline?.active_memories ?? 0;
  const guidance = (pipeline?.learning_guidance || '').trim();
  if (pending <= 0 && eligible <= 0 && almostReady <= 0) return null;

  return (
    <section
      aria-label="Self-learning pipeline"
      className="rounded-lg border border-violet-200 bg-violet-50/70 p-3 dark:border-violet-900/40 dark:bg-violet-950/25"
      data-testid="assistant-context-promotion-pipeline"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200">
        Self-learning in progress
      </p>
      <ul className="mt-2 space-y-1 text-xs text-violet-950 dark:text-violet-100">
        {pending > 0 ? (
          <li>
            <span className="font-medium">{pending}</span> pattern{pending === 1 ? '' : 's'} being
            watched from your activity
          </li>
        ) : null}
        {almostReady > 0 ? (
          <li>
            <span className="font-medium">{almostReady}</span> almost ready to promote into learned
            preferences
          </li>
        ) : null}
        {eligible > 0 ? (
          <li>
            <span className="font-medium">{eligible}</span> meet promotion thresholds now
          </li>
        ) : null}
        {active > 0 ? (
          <li className="text-[11px] text-violet-800/90 dark:text-violet-200/90">
            {active} active preference{active === 1 ? '' : 's'} already guiding answers
          </li>
        ) : null}
      </ul>
      {guidance ? (
        <p className="mt-2 text-[11px] text-violet-800/90 dark:text-violet-200/90" data-testid="assistant-context-pipeline-guidance">
          {guidance}
        </p>
      ) : eligible > 0 ? (
        <p className="mt-2 text-[11px] text-violet-800/90 dark:text-violet-200/90">
          Keep using the assistant and CRM — promoted patterns appear here and in Settings after the
          next replay batch.
        </p>
      ) : null}
      <Link
        to={brainLearnedPreferencesSettingsPath()}
        className="mt-2 inline-block text-[11px] font-medium text-violet-700 underline decoration-violet-400/60 underline-offset-2 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-100"
        data-testid="assistant-context-pipeline-settings-link"
      >
        View learning status in Settings
      </Link>
    </section>
  );
}

/** Footnote when orchestration runs token-aware context assembly [2.2.1233]. */
export function AssistantContextHarnessNote({ harness }: { harness?: BrainContextHarness | null }) {
  if (harness?.mode !== 'v4_token' || typeof harness.token_budget !== 'number') return null;
  return (
    <p
      className="text-[10px] leading-relaxed text-gray-400 dark:text-gray-500"
      data-testid="assistant-context-harness-note"
    >
      Context balanced across deal focus, insights, and learned preferences ({harness.token_budget}{' '}
      tokens).
    </p>
  );
}

/** Rep contact rhythm from world_model.rep_state — advisor timing context [2.2.1246]. */
export function AssistantContextRepContactPatterns({
  patterns,
}: {
  patterns?: AssistantRepContactPatterns | null;
}) {
  return (
    <BrainRepContactPatternsHint
      patterns={patterns}
      testId="assistant-context-rep-patterns"
    />
  );
}
